/* ==========================================================================
   MIKORI — "Build a Bundle" floating sticker
   --------------------------------------------------------------------------
   Markup : snippets/mikori-bundle-sticker.liquid
   Styles : assets/mikori-bundle-sticker.css
   --------------------------------------------------------------------------
   Two jobs only:
     1. Dismiss the sticker for the rest of the session.
     2. Keep it clear of the theme's fixed bottom bars.

   Deliberately a standalone file rather than an append to assets/mikori.js:
   that file is a minified build artifact with a committed .map, so an append
   would be destroyed by the next build.

   Everything here is progressive enhancement — with JS off the sticker still
   renders and the link still works.
   ========================================================================== */

(function () {
  'use strict';

  var STICKER = '.mk-bundle-sticker';
  var DISMISS_KEY = 'mk-bundle-sticker-dismissed';
  var GAP = 12; // clearance above the tallest bar we find

  /* The theme stacks fixed bottom bars that the sticker must not sit on top of:
   *
   *   .mk-pdp-sticky        bottom:0            all breakpoints (PDP)
   *                         bottom:70px         < 750px, lifted over the tab bar
   *   .mk-tab-bar-section   bottom:10px        < 750px only, display:none above
   *
   * On a mobile PDP those two stack to roughly 138px of occupied height, so
   * hardcoding an offset would be wrong on most pages. Measure the real
   * rendered geometry instead — that also survives any future theme change.
   */
  var BARS = ['.mk-pdp-sticky', '.mk-tab-bar-section'];

  var root = document.documentElement;

  function measureBottomOffset() {
    var viewport = window.innerHeight || root.clientHeight;
    var occupied = 0;

    for (var i = 0; i < BARS.length; i++) {
      var bars = document.querySelectorAll(BARS[i]);

      for (var j = 0; j < bars.length; j++) {
        var bar = bars[j];
        var style = window.getComputedStyle(bar);

        // Bars are display:none when they do not apply at this breakpoint.
        if (style.display === 'none' || style.visibility === 'hidden') continue;

        var rect = bar.getBoundingClientRect();
        if (!rect.height) continue;

        // Distance from the bottom of the viewport up to the bar's top edge.
        var fromBottom = viewport - rect.top;
        if (fromBottom > occupied) occupied = fromBottom;
      }
    }

    if (occupied > 0) {
      root.style.setProperty('--mk-sticker-bottom', Math.round(occupied + GAP) + 'px');
    } else {
      // Nothing in the way — fall back to the stylesheet default.
      root.style.removeProperty('--mk-sticker-bottom');
    }
  }

  function dismiss() {
    // Private mode / blocked storage must fail open, not throw.
    try {
      window.sessionStorage.setItem(DISMISS_KEY, '1');
    } catch (e) {}

    root.classList.add('mk-sticker-off');
  }

  /* The PDP sticky bar is NOT simply CSS-driven: it ships with an inline
   * style="display:none" and the theme's own JS reveals it once you scroll past
   * the main add-to-cart. A resize listener would never notice that, so watch
   * the bars directly for attribute and box changes. This avoids a permanent
   * scroll listener, which would force layout reads on every frame.
   */
  function observeBars() {
    var bars = [];

    for (var i = 0; i < BARS.length; i++) {
      var found = document.querySelectorAll(BARS[i]);
      for (var j = 0; j < found.length; j++) bars.push(found[j]);
    }

    if (!bars.length) return;

    if (window.MutationObserver) {
      var observer = new MutationObserver(measureBottomOffset);
      for (var k = 0; k < bars.length; k++) {
        observer.observe(bars[k], {
          attributes: true,
          attributeFilter: ['style', 'class']
        });
      }
    }

    if (window.ResizeObserver) {
      var sizes = new ResizeObserver(measureBottomOffset);
      for (var m = 0; m < bars.length; m++) sizes.observe(bars[m]);
    }
  }

  function init() {
    var sticker = document.querySelector(STICKER);
    if (!sticker) return;

    // Already dismissed by the snippet's pre-paint guard — nothing to do.
    if (root.classList.contains('mk-sticker-off')) return;

    sticker.addEventListener('click', function (event) {
      var target = event.target;
      if (!target || typeof target.closest !== 'function') return;

      var closeBtn = target.closest('[data-mk-sticker-close]');
      if (!closeBtn || !sticker.contains(closeBtn)) return;

      // The close button is a sibling of the link, not a child, so dismissing
      // never risks navigating to the bundle page.
      dismiss();
    });

    measureBottomOffset();

    // The bottom offset changes as bars appear and disappear. Gate the CSS
    // transition behind this class so the sticker does not visibly slide into
    // place on first load, only on later adjustments.
    window.requestAnimationFrame(function () {
      root.classList.add('mk-sticker-ready');
    });

    observeBars();

    window.addEventListener('resize', measureBottomOffset);
    window.addEventListener('orientationchange', measureBottomOffset);
    window.addEventListener('load', measureBottomOffset);

    // Late-arriving bars (lazy sections, theme editor) settle after first paint.
    window.setTimeout(measureBottomOffset, 400);

    // Theme editor: re-observe and re-measure when a section is rendered.
    document.addEventListener('shopify:section:load', function () {
      observeBars();
      measureBottomOffset();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
